import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Theme = 'light' | 'dark';
type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  themePreference: ThemePreference;
  setTheme: (theme: Theme) => void;
  setThemePreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const prefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveTheme = (preference: ThemePreference): Theme => {
  if (preference === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return preference;
};

const applyTheme = (theme: Theme) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [themePreference, setPreferenceState] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem('theme') as ThemePreference;
    const initial: ThemePreference =
      saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
    applyTheme(resolveTheme(initial));
    return initial;
  });

  const [theme, setThemeResolved] = useState<Theme>(() =>
    resolveTheme(themePreference)
  );

  useEffect(() => {
    const resolved = resolveTheme(themePreference);
    setThemeResolved(resolved);
    applyTheme(resolved);
    localStorage.setItem('theme', themePreference);
  }, [themePreference]);

  useEffect(() => {
    if (themePreference !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = resolveTheme('system');
      setThemeResolved(resolved);
      applyTheme(resolved);
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [themePreference]);

  const setThemePreference = (preference: ThemePreference) => {
    setPreferenceState(preference);
  };

  const setTheme = (newTheme: Theme) => {
    setPreferenceState(newTheme);
  };

  const toggleTheme = () => {
    setPreferenceState(resolveTheme(themePreference) === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider
      value={{ theme, themePreference, setTheme, setThemePreference, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
